import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/shared/utils/utils';
import { ZynxIcon, type ZynxIconName, type ZynxTone } from '@/shared/components/ui/ZynxIcon';

/**
 * The one page header every screen uses: title, a one-line description, and
 * the page's primary actions on the right.
 */
export interface PageHeaderProps {
  title: ReactNode;
  /** One sentence — what this screen is for. */
  description?: ReactNode;
  icon?: LucideIcon;
  zynxIcon?: ZynxIconName;
  zynxTone?: ZynxTone;
  /** Buttons / links, right-aligned; wraps under the title on small screens. */
  actions?: ReactNode;
  /** Small line above the title, e.g. "Sales" or a breadcrumb. */
  eyebrow?: ReactNode;
  className?: string;
}

export function PageHeader({
  title,
  description,
  icon: Icon,
  zynxIcon,
  zynxTone = 'indigo',
  actions,
  eyebrow,
  className,
}: PageHeaderProps) {
  return (
    <header className={cn('flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {(Icon || zynxIcon) && (
          <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/15 bg-primary/10 text-primary shadow-2xs">
            <ZynxIcon
              name={zynxIcon}
              icon={Icon}
              tone={zynxTone}
              variant="bare"
              size={22}
            />
          </div>
        )}
        <div className="min-w-0">
          {eyebrow && <p className="mb-1 text-label-caps uppercase text-on-surface-subtle">{eyebrow}</p>}
          <h1 className="break-words font-display text-display-sm font-bold text-on-surface">{title}</h1>
          {description && <p className="mt-1 max-w-3xl text-body-md text-on-surface-variant">{description}</p>}
        </div>
      </div>
      {/* A page's own button group inside `actions` wraps too — on a phone a row of four buttons overflowed. */}
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2 sm:mt-0 [&>div]:flex-wrap">{actions}</div>}
    </header>
  );
}

